/**
 * The Quick Bytes dashboard card's vertical marquee.
 *
 * Decorative. The list is duplicated once and scrolled by exactly half the
 * track height, which is what makes the loop seamless — at the end of the tween
 * the second copy sits precisely where the first started, so the reset is
 * invisible. Change the duplication and the loop visibly jumps.
 *
 * Three behaviours worth knowing, all ROUTE_INVENTORY §2.9 #9:
 *
 * - **`prefers-reduced-motion` is respected** by bailing before the tween is
 *   created, so the list renders statically rather than being animated and then
 *   paused. Both copies stay in the DOM; the mask just stops moving over them.
 * - **Hover pauses**, because reading a title that is sliding away is the one
 *   thing a person might actually want to do here.
 * - The **CSS mask** fades the top and bottom 16px, so items enter and leave
 *   rather than being clipped at a hard edge.
 */

import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { InlineMarkdown } from '@/components/core/markdown/markdown';

/**
 * Static preview content. Was `mockQuickBytesData.concepts`, which dragged the
 * 666-line `mockMindspaceData` module into the production bundle so an
 * always-visible decorative animation could show three titles. The card is not
 * data-mode gated, so real users saw this filler either way.
 */
const PREVIEW_BYTES = [
  {
    id: 'preview-derivative',
    title: 'Derivatives',
    summary: 'The instantaneous rate of change of a function.',
  },
  {
    id: 'preview-entropy',
    title: 'Entropy',
    summary: 'How much uncertainty a distribution carries.',
  },
  {
    id: 'preview-recursion',
    title: 'Recursion',
    summary: 'A function defined in terms of smaller instances of itself.',
  },
] as const;

export function QuickBytesMarqueePreview() {
  const concepts = PREVIEW_BYTES;
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (prefersReducedMotion) return;

    // The track contains the list duplicated twice; half-height = one full loop.
    const halfHeight = track.scrollHeight / 2;
    if (halfHeight <= 0) return;

    // Calm pace: ~3s per item.
    const duration = (concepts.length || 1) * 3;

    gsap.set(track, { y: 0 });
    const tween = gsap.to(track, {
      y: -halfHeight,
      duration,
      ease: 'none',
      repeat: -1,
    });
    tweenRef.current = tween;

    const handleEnter = () => tween.pause();
    const handleLeave = () => tween.resume();
    viewport.addEventListener('mouseenter', handleEnter);
    viewport.addEventListener('mouseleave', handleLeave);

    return () => {
      viewport.removeEventListener('mouseenter', handleEnter);
      viewport.removeEventListener('mouseleave', handleLeave);
      tween.kill();
      tweenRef.current = null;
    };
  }, [concepts.length]);

  // Duplicate for seamless infinite loop.
  const loopedConcepts = [...concepts, ...concepts];

  return (
    <div className='flex w-full flex-col items-center gap-3 px-2'>
      {/* Hidden from assistive tech: it is decorative filler, and the list is
          duplicated for the seamless loop, so a screen reader would read every
          item twice. Same treatment as QuickBytesTile's decorative stack. */}
      <div
        ref={viewportRef}
        data-marquee-viewport
        aria-hidden='true'
        className='relative w-full overflow-hidden'
        style={{
          height: 260,
          maskImage:
            'linear-gradient(to bottom, transparent 0, black 16px, black calc(100% - 16px), transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0, black 16px, black calc(100% - 16px), transparent 100%)',
        }}
      >
        <div ref={trackRef} data-marquee-track className='flex flex-col gap-2'>
          {loopedConcepts.map((concept, idx) => (
            <div
              key={`${concept.id}-${idx}`}
              className='rounded-lg border border-border bg-gray-50/50 p-3.5 dark:bg-white/[0.04]'
            >
              <div className='mb-1 text-sm font-semibold text-foreground'>
                <InlineMarkdown>{concept.title}</InlineMarkdown>
              </div>
              <div className='line-clamp-2 text-xs text-muted-foreground'>
                <InlineMarkdown>{concept.summary}</InlineMarkdown>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
